import { Option } from 'monapt'

import { LinkedAccount } from 'generated/types'

import * as plaid from './plaidTypes'

const commonStaticFields = {
  screen: 'linkedCards',
  screen_name: 'linkedCards',
}

const commonDynamicFields = (linkedAccount: LinkedAccount) => {
  const financialInstitutionOption = Option(linkedAccount.financialInstitution)

  return {
    institution_id: financialInstitutionOption
      .map((financialInstitution) => financialInstitution.id)
      .getOrElse(() => ''),

    institution_name: financialInstitutionOption
      .map((financialInstitution) => financialInstitution.name)
      .getOrElse(() => ''),

    error_code: linkedAccount.errorCode || '',
    link_status: linkedAccount.status || '',
  }
}

export const linkedCardsFixFields = (linkedAccount: LinkedAccount) => ({
  ...commonStaticFields,
  ...commonDynamicFields(linkedAccount),
  object_name: 'linkedCardsFix',
})

export const linkedCardsLoginDetailsModalFields = (
  linkedAccount: LinkedAccount,
) => ({
  ...commonStaticFields,
  ...commonDynamicFields(linkedAccount),
  object_name: 'linkedCardsLoginDetailsModal',
})

export const linkedAccountsScreenViewedFields = () => ({
  ...commonStaticFields,
  object_name: 'linkedCards',
})

export const unlinkButtonTappedFields = (ctaTitle: string) => ({
  ...commonStaticFields,
  object_name: 'linkedCardsUnlinked',
  cta_title: ctaTitle,
})

export const subAccountToggleTappedFields = (
  subaccountSubtype: string,
  enabled: boolean,
) => ({
  ...commonStaticFields,
  object_name: 'linkedCardsToggle',
  subaccount_subtype: `${subaccountSubtype}`,
  state: `${enabled ? 'on' : 'off'}`,
  default_state: `${enabled ? 'off' : 'on'}`,
})

export const addCardTappedFields = () => ({
  ...commonStaticFields,
  object_name: 'linkedCardsAddCard',
  cta_title: 'Add Accounts',
})

export const institutionViewedFields = (
  subaccountCount: number,
  linkedAccount: LinkedAccount,
) => ({
  ...commonStaticFields,
  ...commonDynamicFields(linkedAccount),
  object_name: 'linkedCardsInstitution',
  sub_account_count: `${subaccountCount}`,
})

const plaidScreenInfo = {
  screen: 'linkedAccountsLinkBankAccountLogin',
  screen_name: 'linkedAccountsLinkBankAccountLogin',
}

export const onPlaidSuccessFields = (metadata: plaid.OnSuccessMetadata) => {
  // this should always be populated but let's be safe since it's a 3rd party
  const institution = Option(metadata.institution)
  return {
    ...plaidScreenInfo,
    object_name: 'linkedAccountsPlaidConnected',
    institution_id: institution
      .map(({ institution_id }) => institution_id)
      .getOrElse(() => ''),
    institution_name: institution.map(({ name }) => name).getOrElse(() => ''),
    link_session_id: metadata.link_session_id,
  }
}

export const onPlaidExitFields = (
  error: plaid.Error | null,
  metadata: plaid.OnExitMetadata,
) => {
  // error is null if user closes the plaid link window
  const errorOption = Option(error)
  // this should always be populated but let's be safe since it's a 3rd party
  const institution = Option(metadata.institution)

  return {
    ...plaidScreenInfo,
    object_name: 'linkedAccountsPlaidExit',
    request_id: metadata.request_id,
    institution_id: institution
      .map(({ institution_id }) => institution_id)
      .getOrElse(() => ''),
    institution_name: institution.map(({ name }) => name).getOrElse(() => ''),
    status: metadata.status,
    link_session_id: metadata.link_session_id,
    error_type: errorOption
      .map(({ error_type }) => error_type)
      .getOrElse(() => ''),
    error_code: errorOption
      .map(({ error_code }) => error_code)
      .getOrElse(() => ''),
    error_message: errorOption
      .map(({ error_message }) => error_message)
      .getOrElse(() => ''),
  }
}

export const onPlaidEventFields = (
  eventName: plaid.EventName,
  metadata: plaid.OnEventMetadata,
) => ({
  ...plaidScreenInfo,
  object_name: 'linkedAccountsPlaidEvent',
  request_id: metadata.request_id,
  institution_id: metadata.institution_id,
  institution_name: metadata.institution_name,
  event_name: eventName,
  view_name: metadata.view_name,
  exit_status: metadata.exit_status,
  mfa_type: metadata.mfa_type,
  link_session_id: metadata.link_session_id,
  error_type: metadata.error_type,
  error_code: metadata.error_code,
  error_message: metadata.error_message,
})
